import {
  keepPreviousData,
  queryOptions,
  useQuery,
} from '@tanstack/react-query';
import { useAtomValue } from 'jotai';
import { useCallback, useState } from 'react';
import { api, failureMessage } from 'lib/request/api';

import {
  draftRebuildsEndedAtom,
  draftReloadAtom,
  draftTablesReloadAtom,
} from './atoms';
import { hasOwnershipChanges } from './ownership-changes-utils';
import type {
  CloneNotices,
  DraftReadResponse,
  OwnershipChangesResponse,
} from './types';

/** A read's answer: the data once read, or why the read failed. */
interface DraftRead<T> {
  data: T | null;
  error: string | null;
}

/**
 * GETs `path`; a failure's message is the app's own for it (the server's reason,
 * else its status, else "Failed to fetch resource").
 *
 * @param {string} path - The path, with no leading slash
 * @param {AbortSignal} signal - Aborts it
 * @return {Promise} The body
 */
const getDraftPath = async <T>(
  path: string,
  signal: AbortSignal
): Promise<T> => {
  try {
    return await api.get(path, { signal }).json<T>();
  } catch (error) {
    throw new Error(await failureMessage(error), { cause: error });
  }
};

/**
 * A read of the draft that is read again on each bump of a count of its
 * changes: the count is in the key, and the last answer stays shown while the
 * next one loads. Nothing is kept once the reader is gone, so a later visit
 * reads afresh (Next can rebuild the same draft).
 *
 * @param {string} path - The path, with no leading slash
 * @param {number} changes - The count whose bumps read it again
 * @return {object} The query options
 */
const draftReadOptions = <T>(path: string, changes: number) =>
  queryOptions({
    queryKey: ['packages', 'drafts', path, changes],
    queryFn: ({ signal }) => getDraftPath<T>(path, signal),
    placeholderData: keepPreviousData,
    gcTime: 0,
  });

/**
 * A clone's notices, from the draft read asked for them (`include_notices`;
 * the status checks of a wait leave it off, so the server works none out for
 * them): what the sources leave unticked, and what the As of Date removed and
 * added. Read only for a clone, again once each build or scenario switch has
 * ended (`draftRebuildsEndedAtom`): a tick cannot change them, so a tick reads
 * nothing. Null before the read answers and on a draft that is not a ready
 * clone.
 *
 * @param {string | null} draftId - The draft
 * @param {boolean} clone - Whether the draft is built from source packages
 * @return {DraftRead} The notices, or why they could not be read
 */
export const useCloneNotices = (
  draftId: string | null,
  clone: boolean
): DraftRead<CloneNotices> => {
  const rebuildsEnded = useAtomValue(draftRebuildsEndedAtom);
  const { data, error } = useQuery({
    ...draftReadOptions<DraftReadResponse>(
      `packages/drafts/${draftId}?include_notices=true`,
      rebuildsEnded
    ),
    enabled: clone && draftId !== null,
  });
  return {
    data: data
      ? {
          preservedExclusions: data.preservedExclusions,
          ownershipChanges: data.ownershipChanges,
        }
      : null,
    error: error ? error.message : null,
  };
};

/**
 * Which of a clone's two notices show, the exclusions and the ownership
 * warning: each closes on its own; the header's Hide Notifications hides both,
 * and Show brings both back. The toggle reads Hide while either is on screen.
 *
 * @param {CloneNotices | null} notices - The notices read, null before or with none
 * @return {object} Each notice's flag and close, whether either exists
 *   (`toggleable`) or is on screen (`shown`), and the toggle
 */
export const useCloneNoticesShown = (notices: CloneNotices | null) => {
  const [exclusions, setExclusions] = useState(true);
  const [ownership, setOwnership] = useState(true);
  const hasExclusions = (notices?.preservedExclusions ?? null) !== null;
  const hasOwnership = hasOwnershipChanges(notices?.ownershipChanges ?? null);
  const shown = (hasExclusions && exclusions) || (hasOwnership && ownership);

  const toggle = useCallback(() => {
    setExclusions(!shown);
    setOwnership(!shown);
  }, [shown]);
  const closeExclusions = useCallback(() => setExclusions(false), []);
  const closeOwnership = useCallback(() => setOwnership(false), []);

  return {
    exclusions,
    ownership,
    closeExclusions,
    closeOwnership,
    toggleable: hasExclusions || hasOwnership,
    shown,
    toggle,
  };
};

/**
 * A clone's Assets Removed and Assets Added, each list whole: read when the
 * review opens, and again after each settled change to the draft while it is
 * open, a refused Add selected included (the review may have been behind).
 *
 * @param {string} draftId - The draft
 * @return {DraftRead} The lists, or why they could not be read
 */
export const useOwnershipChanges = (
  draftId: string
): DraftRead<OwnershipChangesResponse> => {
  // Each of the two counts only goes up, so their sum moves on every bump.
  const changes =
    useAtomValue(draftReloadAtom) + useAtomValue(draftTablesReloadAtom);
  const { data, error } = useQuery(
    draftReadOptions<OwnershipChangesResponse>(
      `packages/drafts/${draftId}/ownership_changes`,
      changes
    )
  );
  return { data: data ?? null, error: error ? error.message : null };
};
